import { Box, Collapse, Button, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import _ from 'lodash';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { CenteredNarrowContent } from 'theme/elements/Content';
import { invalidFields } from 'components/FormCreator/types';
import { newBankFieldsValidations, SettingsTabType } from 'constants/settings.constants';
import { setListOfInvalids, validateFields } from 'components/FormCreator/validation';
import { FormButton } from 'theme/elements/Buttons';
import { AddBankFields } from 'constants/settings.constants';
import NewBankDetailsForm from './NewBankDetailsForm';
import FileUploadBox from 'components/FileUploadBox';
import { getExistingBankAccounts, getMessage, isLoading } from 'state/reducers/bank-account.reducers';
import { CustomAlert } from 'theme/elements/Alerts';
import { pagesEnum, userMessage } from 'constants/ui.constants';
import { getBankList } from 'state/reducers/global.reducers';
import MobileNavbar from 'sections/layout/MobileNavbar';
import { getMobile, togglePage } from 'state/reducers/ui.reducers';
import { addBankAccount } from 'services/bank-account.services';
import { maxFileSize } from 'constants/global.constants';
import { Page } from 'theme/elements/Page';

export default function AddBankAccount(props: { handleCancel?: any }) {
  const loading: boolean = useAppSelector(isLoading);
  const t = useAppSelector(selectTranslations);
  const [accountOwnerName, setAccountOwnerName] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [branchNumber, setBranchNumber] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [bankId, setBankId] = useState(null);
  const [disabledSubmit, setDisabledSubmit] = useState(true);
  const userMessage: userMessage = useAppSelector(getMessage);
  const bankList = useAppSelector(getBankList);
  const isMobile = useAppSelector(getMobile);
  const dispatch = useAppDispatch();
  const prefixFields = 'page.settings.bank_accounts.create.';
  const baseBankAccounts = useAppSelector(getExistingBankAccounts);


  const handleEventCancel = () => {
    if (isMobile) {
      dispatch(togglePage(`/${pagesEnum.SETTINGS}/${SettingsTabType.BankAccounts}`));
    } else {
      props.handleCancel();
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    updateInput(name, value);
    if (_.has(newBankFieldsValidations, name)) {
      setInvalidFields(setListOfInvalids(invalidFields, newBankFieldsValidations, name, value));
      if (name === AddBankFields.ACCOUNT_NUMBER) {
        if (
          _.find(
            _.values(baseBankAccounts),
            (user) => _.isEqual(user.accountNumber, value)
          )
        ) {
          setInvalidFields({
            ...invalidFields,
            [AddBankFields.ACCOUNT_NUMBER]: {
              errorText: 'servers.errors.codes.1200',
              valid: false,
              name: AddBankFields.ACCOUNT_NUMBER
            }
          }
          )
        }

      }
    }
  };

  const updateInput = (name, value) => {
    if (name === AddBankFields.ACCOUNT_HOLDER_NAME) {
      setAccountOwnerName(value);
    } else if (name === AddBankFields.BANK_NAME) {
      setBankId(value);
    } else if (name === AddBankFields.BRANCH_NUMBER) {
      setBranchNumber(value);
    } else {
      setAccountNumber(value);
    }
  };

  const handleFileUpload = (fileUploaded: File) => {
    if (fileUploaded?.size <= maxFileSize) {
      setFile(fileUploaded);
      setIsFileUploaded(true);
    } else {
      if (file && fileUploaded) {
        setFile(null);
        setIsFileUploaded(false);
      }
    }
  };

  const checkFields = () => {
    let obj = {
      [AddBankFields.ACCOUNT_HOLDER_NAME]: accountOwnerName,
      [AddBankFields.BANK_NAME]: bankName,
      [AddBankFields.ACCOUNT_NUMBER]: accountNumber,
      [AddBankFields.BRANCH_NUMBER]: branchNumber,
      [AddBankFields.FILE]: file?.name || '',
    };
    return validateFields(newBankFieldsValidations, obj);
  };

  const handleSaveClick = () => {
    const invalidFields = checkFields();
    if (_.keys(invalidFields).length) {
      setInvalidFields(invalidFields);
    } else {
      if (bankId !== null && file !== null) {
        dispatch(addBankAccount(accountOwnerName, accountNumber, branchNumber, bankId, file));
      }
    }
  };

  const [invalidFields, setInvalidFields] = useState<invalidFields>({});

  useEffect(() => {
    const invalidFields = checkFields();
    if (_.keys(invalidFields).length) {
      setDisabledSubmit(true);
    } else {
      setDisabledSubmit(false);
    }
  }, [accountOwnerName, bankId, accountNumber, branchNumber, file]);

  return (
    <Page>
      <Box>
        <MobileNavbar handleBack={handleEventCancel} handleCancel={handleEventCancel} />
        <Stack>
          <CenteredNarrowContent>
            <Collapse in={userMessage.exist}>
              <CustomAlert
                icon={userMessage?.icon}
                severity={userMessage.type}
                text={t(userMessage.text || '')}
              />
            </Collapse>
            <Box style={{ marginTop: userMessage.exist ? '24px' : '0px' }}>
              <NewBankDetailsForm
                bankList={bankList}
                handleChange={handleChange}
                invalidFields={invalidFields}
                inputsData={{
                  accountOwnerName,
                  bankId,
                  bankName,
                  accountNumber,
                  branchNumber,
                }}
              />
            </Box>
            <FileUploadBox
              labelPrefix={prefixFields + 'management_approval.'}
              imageKey={`${prefixFields}icon`}
              handleTransferProofFile={handleFileUpload}
              isFileUploaded={isFileUploaded}
            />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto auto',
                justifyContent: 'space-between',
                marginTop: '25px',
              }}
            >
              <FormButton>
                <Button variant="outlined" onClick={handleEventCancel}>
                  {t(prefixFields + 'cancel_button.text')}
                </Button>
              </FormButton>
              <FormButton>
                <Button
                  variant="contained"
                  onClick={handleSaveClick}
                  disabled={disabledSubmit || loading}
                >
                  {t(prefixFields + 'create_button.text')}
                </Button>
              </FormButton>
            </Box>
          </CenteredNarrowContent>
        </Stack>
      </Box>
    </Page>
  );
}
